import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import _ from 'lodash';
import { Radio, Spin, Tooltip, Tag } from 'antd';
import callApi from 'helpers/callapi';
import { formatComma } from 'helpers/helpers';

// ปีที่แล้วที่ snapshot ไว้ (tasks/exams/snapshot-site-history.js ฝั่ง API)
const HISTORY_YEAR = '69';
const DAYS = [1, 2, 3, 4, 5, 6];
// วันสอบตามช่วงที่เปิดใช้งาน (config center_active_test) — TGAT/TPAT = วันที่ 1-3, A-Level = วันที่ 4-6
const DAYS_BY_ACTIVE_TEST = {
  TGATTPAT: { days: [1, 2, 3], label: 'TGAT/TPAT' },
  ALEVEL: { days: [4, 5, 6], label: 'A-Level' },
};

const EMPTY_STAT = { rooms: 0, seats: 0, special_rooms: 0, special_seats: 0, cbt_rooms: 0, cbt_seats: 0 };

// ปี 70 ไม่มี CBT แล้ว → รวมห้อง/ที่นั่ง CBT เข้ากับปกติ แยกเฉพาะห้องพิเศษ
const mergeStat = (stat) => {
  const s = { ...EMPTY_STAT, ...(stat || {}) };
  return {
    rooms: (s.rooms || 0) + (s.cbt_rooms || 0),
    seats: (s.seats || 0) + (s.cbt_seats || 0),
    special_rooms: s.special_rooms || 0,
    special_seats: s.special_seats || 0,
    cbt_rooms: s.cbt_rooms || 0,
    cbt_seats: s.cbt_seats || 0,
  };
};

const addStat = (a, b) => _.mergeWith({ ...a }, b, (x, y) => (x || 0) + (y || 0));

const emptyAgg = () => ({
  site_count: 0,
  total: mergeStat(),
  days: DAYS.reduce((acc, d) => ({ ...acc, [d]: mergeStat() }), {}),
});

const aggregate = (agg, site) => ({
  site_count: agg.site_count + 1,
  total: addStat(agg.total, site.total),
  days: DAYS.reduce((acc, d) => ({ ...acc, [d]: addStat(agg.days[d], site.days[d]) }), {}),
});

const normalizeSite = (site) => ({
  ...site,
  total: mergeStat(site.stats),
  days: DAYS.reduce((acc, d) => ({ ...acc, [d]: mergeStat(_.get(site, `info_by_days.day${d}`)) }), {}),
});

const num = (value) => (value ? formatComma(value) : '-');

export default () => {
  const me = useSelector((state) => state.user);
  const adminConfig = useSelector((state) => state.configs || {});
  const activeTest = DAYS_BY_ACTIVE_TEST[adminConfig.center_active_test];
  const visibleDays = activeTest ? activeTest.days : DAYS;

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [examDays, setExamDays] = useState([]);
  const [view, setView] = useState('site');

  useEffect(() => {
    const getHistory = async () => {
      setLoading(true);
      try {
        const result = await callApi({ url: `/exam-centers/me/history/${HISTORY_YEAR}/sites` });
        setSites((result.data || []).map(normalizeSite));
        setExamDays(result.exam_days || []);
      } catch (err) {
        console.error(err);
      }
      setLoading(false);
    };
    getHistory();
  }, []);

  const examDaysByIndex = useMemo(() => _.keyBy(examDays, 'day'), [examDays]);

  // แสดงเฉพาะวันของช่วงสอบที่เปิดใช้งาน และต้องมีที่นั่งอย่างน้อย 1 สนาม
  const activeDays = useMemo(
    () => visibleDays.filter((d) => sites.some((site) => site.days[d].seats || site.days[d].special_seats)),
    [sites, visibleDays],
  );

  const provinces = useMemo(() => {
    const grouped = _.groupBy(sites, 'province');
    return _.sortBy(
      _.map(grouped, (provinceSites, province) => ({
        province,
        ...provinceSites.reduce(aggregate, emptyAgg()),
      })),
      (p) => -p.total.seats,
    );
  }, [sites]);

  const grandTotal = useMemo(() => sites.reduce(aggregate, emptyAgg()), [sites]);

  const renderDayHeaders = () =>
    activeDays.map((d) => {
      const info = examDaysByIndex[d];
      // แสดงชื่อย่อวิชาไม่เกิน 3 วิชา — วันที่มีวิชาภาษาเยอะดูรายชื่อเต็มได้จาก tooltip
      const shortSubjects = info ? info.subjects.map((s) => s.split(' ')[0]) : [];
      const subjects = shortSubjects.slice(0, 3).join(' / ') + (shortSubjects.length > 3 ? ' / …' : '');
      return (
        <th key={d} className="text-right _nowrap">
          <Tooltip title={info ? `${info.date} : ${info.subjects.join(', ')}` : null}>
            <span>
              วันที่ {d}
              {info && (
                <>
                  <br />
                  <small style={{ fontWeight: 'normal' }}>{subjects}</small>
                </>
              )}
            </span>
          </Tooltip>
        </th>
      );
    });

  const renderStatCells = (row) => (
    <>
      <td className="_nowrap text-right">{num(row.total.rooms)}</td>
      <td className="_nowrap text-right">
        <b>{num(row.total.seats)}</b>
      </td>
      <td className="_nowrap text-center">
        {row.total.special_rooms || 0} : {row.total.special_seats || 0}
      </td>
      {activeDays.map((d) => (
        <td key={d} className="_nowrap text-right">
          {num(row.days[d].seats)}
        </td>
      ))}
    </>
  );

  const renderTotalRow = (label) => (
    <tr style={{ fontWeight: 'bold', background: '#fff6e5' }}>
      <td colSpan={view === 'site' ? 5 : 1}>{label}</td>
      {view === 'province' && <td className="text-right">{num(grandTotal.site_count)}</td>}
      {renderStatCells(grandTotal)}
    </tr>
  );

  return (
    <div>
      <Spin spinning={loading} delay={0}>
        <div>
          <div className="t-main">
            <div className="flex justify-between">
              <h1>ข้อมูลสนามสอบปี {HISTORY_YEAR} (ปีที่แล้ว)</h1>
            </div>

            {me && me.center && (
              <div className="t-form border-blue" style={{ display: 'block' }}>
                รหัสศูนย์สอบ : {me.center.center_code}
                <br />
                ศูนย์สอบ : {me.center.center_name}
                <br />
                <br />
                <b>สรุปปี {HISTORY_YEAR}</b> : สนามสอบ {formatComma(grandTotal.site_count)} แห่ง ใน{' '}
                {formatComma(provinces.length)} จังหวัด | ห้องสอบ {formatComma(grandTotal.total.rooms)} ห้อง | ที่นั่งสอบ{' '}
                <b>{formatComma(grandTotal.total.seats)}</b> ที่นั่ง | ห้องพิเศษ {formatComma(grandTotal.total.special_rooms)} ห้อง (
                {formatComma(grandTotal.total.special_seats)} ที่นั่ง)
                <br />
                <small style={{ color: '#828897' }}>
                  ห้อง/ที่นั่ง = ห้องสอบต้นแบบของสนาม โดยรวมห้องสอบคอมพิวเตอร์ (CBT {formatComma(grandTotal.total.cbt_rooms)} ห้อง /{' '}
                  {formatComma(grandTotal.total.cbt_seats)} ที่นั่ง) เข้ากับห้องสอบปกติแล้ว เนื่องจากปีนี้ไม่มีการสอบ CBT · คอลัมน์รายวัน =
                  ที่นั่งที่เปิดสอบจริงในวันนั้น{activeTest ? ` (ช่วง ${activeTest.label})` : ''}
                </small>
              </div>
            )}

            <div className="flex justify-between">
              <h2>
                <span>
                  <svg width="22" height="21" viewBox="0 0 22 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M18.75 8.50001H16.5V4.00001C16.5 3.75251 16.378 3.52101 16.1735 3.38101L11.4235 0.131006C11.168 -0.0439941 10.8315 -0.0439941 10.5765 0.131006L5.8265 3.38101C5.622 3.52101 5.5 3.75251 5.5 4.00001V8.50001H3.25C1.7335 8.50001 0.5 9.73401 0.5 11.25V20.25C0.5 20.664 0.8355 21 1.25 21H20.75C21.1645 21 21.5 20.664 21.5 20.25V11.25C21.5 9.73401 20.2665 8.50001 18.75 8.50001ZM11 9.00001C9.8955 9.00001 9 8.10451 9 7.00001C9 5.89551 9.8955 5.00001 11 5.00001C12.1045 5.00001 13 5.89551 13 7.00001C13 8.10451 12.1045 9.00001 11 9.00001Z"
                      fill="#FF8A00"
                    />
                  </svg>
                </span>{' '}
                {view === 'site' ? `มีสนามสอบ ${sites.length} แห่ง` : `ครอบคลุม ${provinces.length} จังหวัด`}
              </h2>
              <div className="action items-center">
                <Radio.Group onChange={(e) => setView(e.target.value)} value={view} buttonStyle="solid" className="mb-4">
                  <Radio.Button value="site">สนามสอบ</Radio.Button>
                  <Radio.Button value="province">จังหวัด</Radio.Button>
                </Radio.Group>
              </div>
            </div>

            {!loading && sites.length === 0 && (
              <div className="t-form" style={{ display: 'block' }}>
                ยังไม่มีข้อมูลสนามสอบปี {HISTORY_YEAR} ของศูนย์สอบนี้ในระบบ
              </div>
            )}

            {sites.length > 0 && (
              <table className="t-table t-table-striped">
                <thead className="th-orange">
                  {view === 'site' ? (
                    <tr>
                      <th>ลำดับ</th>
                      <th>ชื่อสนามสอบ</th>
                      <th>รหัส</th>
                      <th>จังหวัด</th>
                      <th>อำเภอ</th>
                      <th className="text-right">ห้อง</th>
                      <th className="text-right">ที่นั่งสอบ</th>
                      <th className="text-center">
                        <Tooltip title="ห้องสอบสำหรับผู้ต้องการความช่วยเหลือพิเศษ (ห้อง : ที่นั่ง)">
                          <i className="i-disabled"></i>
                        </Tooltip>
                      </th>
                      {renderDayHeaders()}
                    </tr>
                  ) : (
                    <tr>
                      <th>จังหวัด</th>
                      <th className="text-right">สนามสอบ</th>
                      <th className="text-right">ห้อง</th>
                      <th className="text-right">ที่นั่งสอบ</th>
                      <th className="text-center">
                        <Tooltip title="ห้องสอบสำหรับผู้ต้องการความช่วยเหลือพิเศษ (ห้อง : ที่นั่ง)">
                          <i className="i-disabled"></i>
                        </Tooltip>
                      </th>
                      {renderDayHeaders()}
                    </tr>
                  )}
                </thead>
                <tbody>
                  {view === 'site'
                    ? sites.map((site, index) => (
                        <tr key={site.site_code}>
                          <td>{index + 1}</td>
                          <td>
                            {site.school_name}{' '}
                            {site.exam_type === 'cbt' && (
                              <Tooltip title="ปี 69 เป็นสนามสอบคอมพิวเตอร์ (CBT)">
                                <Tag color="blue">CBT</Tag>
                              </Tooltip>
                            )}
                          </td>
                          <td>{site.site_school_code}</td>
                          <td>{site.province}</td>
                          <td>{site.amphur}</td>
                          {renderStatCells(site)}
                        </tr>
                      ))
                    : provinces.map((province) => (
                        <tr key={province.province}>
                          <td>{province.province}</td>
                          <td className="text-right">{num(province.site_count)}</td>
                          {renderStatCells(province)}
                        </tr>
                      ))}
                  {renderTotalRow('รวมทั้งศูนย์สอบ')}
                </tbody>
              </table>
            )}
            <br />
          </div>
        </div>
      </Spin>
    </div>
  );
};
